import { outputReport, getOutputFormat, getPreOpenedWindow } from '@/utils/reportOutput';
import { formatNomenclatureWithSize } from '@equip/utils';
import { entityService, type ListParams } from '@/services/EntityService';
import type { Entity } from '@/types/Entity';
import {
  buildAllIeReportHtml,
  type AllIeItem,
  type AllIeKitSection,
  type AllIeTemplateOptions,
} from '@/reports/templates/allIeTemplate';

const listAllEntities = async (params: Omit<ListParams, 'limit' | 'offset'>): Promise<Entity[]> => {
  return entityService.listAll(params);
};

const parseCsvList = (value: string): string[] =>
  value
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);

/**
 * Normalizes IE Kit filter input to a standardized array of non-empty strings.
 * Handles both array input (from multiselect) and comma-separated string (for backwards compatibility).
 * Returns empty array if input is null/undefined or contains only empty values.
 */
const normalizeIeFilter = (value: unknown): string[] => {
  if (value == null) {
    return [];
  }

  if (Array.isArray(value)) {
    return value
      .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
      .map((v) => v.trim());
  }

  if (typeof value === 'string') {
    return parseCsvList(value);
  }

  console.warn('Unexpected ieFilter type:', typeof value);
  return [];
};

/**
 * Formats an array of strings for display in report headers.
 * Returns null if array is empty, otherwise joins with ', '.
 */
const formatArrayForDisplay = (arr: string[]): string | null => {
  return arr.length > 0 ? arr.join(', ') : null;
};

const chunk = <T>(values: T[], size: number): T[][] => {
  if (size <= 0) return [values];
  const chunks: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
};

const coerceString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const parsePositiveInt = (value: unknown): number | null => {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    const intValue = Math.trunc(value);
    return intValue >= 1 ? intValue : null;
  }
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed >= 1 ? parsed : null;
  }
  return null;
};

const parseCatalogUnitCost = (
  entityData: Record<string, unknown> | null | undefined,
): number | null => {
  const raw = entityData?.unit_price;
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : null;
  }
  if (typeof raw === 'string') {
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const formatRecoverableLabel = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const normalized = value.trim().toUpperCase();
  if (normalized.includes('NON')) return 'NonRecoverable';
  if (normalized.includes('RECOVERABLE')) return 'Recoverable';
  return value.trim();
};

const matchesAnyToken = (value: string, tokens: string[]): boolean => {
  const lowerValue = value.toLowerCase();
  return tokens.some((token) => lowerValue.includes(token.toLowerCase()));
};

export const runAllIeReport = async (params: Record<string, unknown>): Promise<void> => {
  // Normalize IE Kit filter input (handles both array from multiselect and CSV string)
  const ieTokens = normalizeIeFilter(params.ieFilter);

  // NOTE: `api_v1.ie_summary` is a per-person view (person IE Kit assignments joined to issue history).
  // Querying it without `person_id` can be extremely expensive and has been observed to 400/time out.
  // For the "All IE Kit" report we only need IE kit definitions + kit items + catalog enrichment.

  const ieKits = await listAllEntities({
    type: 'ie_kit',
    order: 'id.asc',
    filters: { select: 'id,display_name,entity_data' },
  });

  const kitNameById = new Map<string, string>();
  const matchingKitIds = new Set<string>();

  for (const kit of ieKits) {
    // SOC BOI kits are excluded by default for all users (#1090) — no role-based exception.
    if (kit.entity_data?.soc_tag === true) continue;

    const kitName = kit.display_name?.trim() || 'Unknown Kit';
    kitNameById.set(kit.id, kitName);

    if (ieTokens.length === 0 || matchesAnyToken(kitName, ieTokens)) {
      matchingKitIds.add(kit.id);
    }
  }

  const ieKitItems = await listAllEntities({
    type: 'ie_kit_item',
    order: 'id.asc',
    filters: { select: 'id,display_name,entity_data' },
  });

  const kitItemsInScope = ieKitItems.filter((kitItem) => {
    const kitId = coerceString(kitItem.entity_data?.ie_kit_id);
    if (!kitId) return false;
    // Excludes items belonging to SOC-tagged kits (kitNameById only holds non-SOC kits).
    if (!kitNameById.has(kitId)) return false;
    if (ieTokens.length === 0) return true;
    return matchingKitIds.has(kitId);
  });

  const catalogIds: string[] = [];
  for (const kitItem of kitItemsInScope) {
    const catalogId = coerceString(kitItem.entity_data?.catalog_id);
    if (catalogId) catalogIds.push(catalogId);
  }

  const unitCostByCatalogId = new Map<string, number | null>();
  const recoverableByCatalogId = new Map<string, string | null>();
  const linByCatalogId = new Map<string, string | null>();
  const nsnByCatalogId = new Map<string, string | null>();
  const nomenclatureByCatalogId = new Map<string, string | null>();

  const uniqueCatalogIds = Array.from(
    new Set(
      catalogIds
        .map((id) => (typeof id === 'string' ? id.trim() : ''))
        .filter((id): id is string => Boolean(id && id.length > 0)),
    ),
  );

  const batches = chunk(uniqueCatalogIds, 75);
  for (const batchIds of batches) {
    const idFilter = batchIds.length === 1 ? `eq.${batchIds[0]}` : `in.(${batchIds.join(',')})`;

    const catalogs = await entityService.list({
      type: 'catalog',
      limit: batchIds.length,
      order: 'id.asc',
      filters: {
        id: idFilter,
        select: 'id,display_name,entity_data',
      },
    });

    for (const catalog of catalogs) {
      const entityData = (catalog.entity_data ?? {}) as Record<string, unknown>;
      unitCostByCatalogId.set(catalog.id, parseCatalogUnitCost(entityData));
      linByCatalogId.set(catalog.id, coerceString(entityData.lin));
      nsnByCatalogId.set(catalog.id, coerceString(entityData.nsn));
      nomenclatureByCatalogId.set(
        catalog.id,
        formatNomenclatureWithSize(
          coerceString(catalog.display_name),
          coerceString(entityData.size),
        ),
      );

      const rawRecoverable = entityData.recoverable_nonrecoverable;
      recoverableByCatalogId.set(
        catalog.id,
        typeof rawRecoverable === 'string' ? formatRecoverableLabel(rawRecoverable) : null,
      );
    }
  }

  const kitsMap = new Map<string, AllIeKitSection>();

  for (const kitItem of kitItemsInScope) {
    const kitId = coerceString(kitItem.entity_data?.ie_kit_id);
    if (!kitId) continue;

    const kitName = kitNameById.get(kitId) ?? 'Unknown Kit';
    const catalogId = coerceString(kitItem.entity_data?.catalog_id);
    const qtyAuthorized = parsePositiveInt(kitItem.entity_data?.quantity) ?? 1;

    const unitCost = catalogId ? (unitCostByCatalogId.get(catalogId) ?? null) : null;
    const recoverableFlag = catalogId ? (recoverableByCatalogId.get(catalogId) ?? null) : null;
    const lin = catalogId ? (linByCatalogId.get(catalogId) ?? null) : null;
    const nsn = catalogId ? (nsnByCatalogId.get(catalogId) ?? null) : null;
    const nomenclature =
      (catalogId ? (nomenclatureByCatalogId.get(catalogId) ?? null) : null) ??
      kitItem.display_name ??
      '—';

    const totalCostPerQtyAuth =
      typeof unitCost === 'number' && Number.isFinite(unitCost) ? unitCost * qtyAuthorized : null;

    const item: AllIeItem = {
      lin,
      nsn,
      nomenclature,
      recoverableFlag,
      costPerItem: unitCost,
      totalCostPerQtyAuth,
      qtyAuthorized,
    };

    let section = kitsMap.get(kitName);
    if (!section) {
      section = { kitName, totalIeCost: null, items: [] };
      kitsMap.set(kitName, section);
    }

    section.items.push(item);
  }

  const compareNullableText = (
    a: string | null | undefined,
    b: string | null | undefined,
  ): number => {
    const hasA = typeof a === 'string' && a.trim().length > 0;
    const hasB = typeof b === 'string' && b.trim().length > 0;
    if (!hasA && !hasB) return 0;
    if (!hasA) return 1;
    if (!hasB) return -1;
    return a!.trim().localeCompare(b!.trim());
  };

  const kits: AllIeKitSection[] = Array.from(kitsMap.values())
    .map((kit) => {
      kit.items.sort((left, right) => {
        const linCompare = compareNullableText(left.lin ?? null, right.lin ?? null);
        if (linCompare !== 0) return linCompare;
        return compareNullableText(left.nsn ?? null, right.nsn ?? null);
      });

      const total = kit.items.reduce((sum, row) => {
        return typeof row.totalCostPerQtyAuth === 'number' &&
          Number.isFinite(row.totalCostPerQtyAuth)
          ? sum + row.totalCostPerQtyAuth
          : sum;
      }, 0);

      const hasAnyCost = kit.items.some(
        (row) =>
          typeof row.totalCostPerQtyAuth === 'number' && Number.isFinite(row.totalCostPerQtyAuth),
      );

      const totalItemQuantity = kit.items.reduce(
        (sum, row) => sum + (typeof row.qtyAuthorized === 'number' ? row.qtyAuthorized : 0),
        0,
      );
      const totalLinQuantity = new Set(
        kit.items
          .map((row) => (typeof row.lin === 'string' ? row.lin.trim() : ''))
          .filter((lin) => lin.length > 0),
      ).size;

      // UPR (Unit Price Rate): average unit cost across items with a known cost, from the
      // same catalog unit_price source that backs the Cost Per Item column.
      const costedItems = kit.items.filter(
        (row) => typeof row.costPerItem === 'number' && Number.isFinite(row.costPerItem),
      );
      const upr =
        costedItems.length > 0
          ? costedItems.reduce((sum, row) => sum + (row.costPerItem as number), 0) /
            costedItems.length
          : null;

      return {
        ...kit,
        totalIeCost: hasAnyCost ? total : null,
        upr,
        totalItemQuantity,
        totalLinQuantity,
      } satisfies AllIeKitSection;
    })
    .sort((a, b) => a.kitName.localeCompare(b.kitName));

  // Format IE Kit filter for display
  const ieFilterDisplay = formatArrayForDisplay(ieTokens);

  const ieKitLabel = (params.ieKitLabel as string | undefined) ?? 'IE Kit';

  const options: AllIeTemplateOptions = {
    generatedAt: new Date(),
    header: {
      ieFilter: ieFilterDisplay,
    },
    kits,
    ieKitLabel,
  };

  const html = buildAllIeReportHtml(options);
  outputReport(html, 'all-ie-report.html', getOutputFormat(params), getPreOpenedWindow(params));
};
